import { Alert, Button, FieldError, Input, cn } from "@breenova/ui";
import { ArrowRight, Eye, EyeOff, Lock, Mail } from "lucide-react";
import { useState, type ReactNode, type FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router";

import { useAuth } from "@/auth/auth-context";
import { AuthLayout } from "@/routes/auth-layout";

interface FromState {
  from?: { pathname?: string };
}

export function LoginPage() {
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [reveal, setReveal] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(
    event: FormEvent<HTMLFormElement>,
  ): Promise<void> {
    event.preventDefault();
    setError(null);
    setSubmitting(true);

    try {
      await signIn(email.trim(), password);

      // Back to whatever they were trying to reach before the guard intervened.
      const target =
        (location.state as FromState | null)?.from?.pathname ?? "/";
      await navigate(target, { replace: true });
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not sign in.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthLayout
      title="BreeNova"
      subtitle="Your birds, pairs and nests — in your pocket, in the aviary."
      wideTitle="Welcome back"
      wideSubtitle="Sign in to your aviary."
      footer={
        // Split left and right rather than stacked and centred: two separate
        // destinations, and putting them on one line stops either reading as the
        // form's own submit.
        <div className="flex items-center justify-between gap-md">
          <Link to="/forgot-password" className="text-data text-secondary-strong">
            Forgotten password
          </Link>
          <Link to="/register" className="text-data text-secondary-strong">
            Create account
          </Link>
        </div>
      }
    >
      <form onSubmit={handleSubmit} className="flex flex-col gap-2.5" noValidate>
        {error ? <Alert tone="error">{error}</Alert> : null}

        <IconField icon={<Mail className="size-4.75" />}>
          <Input
            id="email"
            name="email"
            type="email"
            autoComplete="username"
            inputMode="email"
            placeholder="you@example.com"
            aria-label="Email"
            required
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            aria-invalid={error ? true : undefined}
            className="h-14 pl-[calc(var(--spacing)*12.75)]"
          />
        </IconField>

        <IconField
          icon={<Lock className="size-4.75" />}
          trailing={
            <button
              type="button"
              onClick={() => setReveal((shown) => !shown)}
              aria-label={reveal ? "Hide password" : "Show password"}
              className="flex size-touch items-center justify-center rounded-full text-on-surface-variant"
            >
              {reveal ? (
                <EyeOff className="size-5" />
              ) : (
                <Eye className="size-5" />
              )}
            </button>
          }
        >
          <Input
            id="password"
            name="password"
            type={reveal ? "text" : "password"}
            autoComplete="current-password"
            placeholder="Your password"
            aria-label="Password"
            required
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            aria-invalid={error ? true : undefined}
            className="h-14 pl-[calc(var(--spacing)*12.75)] pr-touch"
          />
        </IconField>

        {error ? <FieldError>{error}</FieldError> : null}

        <Button
          type="submit"
          block
          size="lg"
          disabled={submitting}
          className="mt-3"
        >
          {submitting ? "Signing in…" : "Sign in"}
          <ArrowRight className="size-5" />
        </Button>
      </form>
    </AuthLayout>
  );
}

/**
 * A field with a leading glyph inside it, and optionally a trailing control.
 *
 * The icon is positioned rather than laid out, so the input keeps its full width and the
 * pill stays one shape — a flex row of icon and input would break the rounded field into
 * two boxes at the focus ring.
 */
export function IconField({
  icon,
  trailing,
  children,
}: {
  icon: ReactNode;
  trailing?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="relative">
      <span
        aria-hidden
        className={cn(
          "pointer-events-none absolute left-5 top-1/2 -translate-y-1/2",
          "text-on-surface-variant",
        )}
      >
        {icon}
      </span>

      {children}

      {trailing ? (
        <span className="absolute right-sm top-1/2 -translate-y-1/2">
          {trailing}
        </span>
      ) : null}
    </div>
  );
}
